import { useCallback } from "react";

import { TableState } from "@tanstack/react-table";
import { z } from "zod";

import { useLocalStorage } from "../../hooks";

import { filterValueWithModeSchema } from "./filterValueWithMode";
import { isNoSaveActive } from "./noSave";

type TableStorageWithColumnFilters<
    TColumnFilters extends Record<string, unknown>,
> = Partial<TableState> & { columnFilters?: TColumnFilters[] };
interface TableStateStorageValue<
    TColumnFilters extends Record<string, unknown>,
> {
    lastUpdated: string;
    state: TableStorageWithColumnFilters<TColumnFilters>;
    /**
     * Non-column data-source params (e.g. an archived flag), keyed by URL param
     * name. A sibling of `state` rather than inside it, so it can't collide with
     * a `TableState` key and survives the destructive `state` replace on mount.
     */
    dataParams?: Record<string, unknown>;
}

const TABLE_STATE_STORAGE_PREFIX = "tst-state:";

// Old localStorage entries may have plain values (e.g. ["Review"]) instead of { value: ["Review"], mode: "is" }.
// This strips column filters that don't match the current FilterValueWithMode shape.
const columnFilterWithModeSchema = z.object({
    id: z.string(),
    value: filterValueWithModeSchema.nullable(),
});

function stripInvalidColumnFilters<T extends { columnFilters?: unknown[] }>(
    state: T
): T {
    if (!state.columnFilters) {
        return state;
    }
    return {
        ...state,
        columnFilters: state.columnFilters.filter(
            (filter) => columnFilterWithModeSchema.safeParse(filter).success
        ),
    };
}

/**
 * Reads a stored view once, without subscribing to it: a `restore` URL only
 * needs a snapshot of the view to encode.
 */
export function readTableStateStorage<
    TColumnFilters extends Record<string, unknown>,
>(
    key: string
): [TableStorageWithColumnFilters<TColumnFilters>, Record<string, unknown>] {
    const raw =
        typeof window === "undefined"
            ? null
            : window.localStorage.getItem(
                  `${TABLE_STATE_STORAGE_PREFIX}${key}`
              );
    // If the localStorage entry is missing, return empty state and dataParams.
    if (raw === null) {
        return [{}, {}];
    }
    try {
        const { state, dataParams } = JSON.parse(
            raw
        ) as TableStateStorageValue<TColumnFilters>;
        return [stripInvalidColumnFilters(state ?? {}), dataParams ?? {}]; // If the entry is malformed, return empty state and dataParams.
    } catch (error) {
        console.error("Error parsing JSON:", error);
        return [{}, {}];
    }
}

export function useTableStateStorage<
    TColumnFilters extends Record<string, unknown>,
>(
    key: string
): [
    TableStorageWithColumnFilters<TColumnFilters>,
    (
        newStateOrUpdater:
            | TableStorageWithColumnFilters<TColumnFilters>
            | ((
                  prev: TableStorageWithColumnFilters<TColumnFilters>
              ) => TableStorageWithColumnFilters<TColumnFilters>)
    ) => void,
    Record<string, unknown>,
    (dataParams: Record<string, unknown>) => void,
] {
    const [storage, setStorage] = useLocalStorage<
        TableStateStorageValue<TColumnFilters>
    >(`${TABLE_STATE_STORAGE_PREFIX}${key}`, {
        lastUpdated: new Date().toISOString(),
        state: {},
    });
    const state = stripInvalidColumnFilters(storage.state);

    /**
     * In `noSave` mode every write is a no-op: the table renders from the URL
     * but never persists, leaving the saved view intact.
     */
    const noSave = isNoSaveActive();

    // Always merge into the previous blob so writing `state` never drops the
    // `dataParams` sibling (and vice-versa).
    const updateStorage = useCallback(
        (
            newStateOrUpdater:
                | TableStorageWithColumnFilters<TColumnFilters>
                | ((
                      prev: TableStorageWithColumnFilters<TColumnFilters>
                  ) => TableStorageWithColumnFilters<TColumnFilters>)
        ) => {
            if (noSave) {
                return;
            }
            setStorage((prevStorage) => ({
                ...prevStorage,
                lastUpdated: new Date().toISOString(),
                state:
                    typeof newStateOrUpdater === "function"
                        ? newStateOrUpdater(prevStorage.state)
                        : newStateOrUpdater,
            }));
        },
        [setStorage, noSave]
    );

    const updateDataParams = useCallback(
        (dataParams: Record<string, unknown>) => {
            if (noSave) {
                return;
            }
            setStorage((prevStorage) => ({
                ...prevStorage,
                lastUpdated: new Date().toISOString(),
                dataParams,
            }));
        },
        [setStorage, noSave]
    );

    return [state, updateStorage, storage.dataParams ?? {}, updateDataParams];
}
